import React, { useRef } from 'react';
import T from 'prop-types';
import { isNil, map, mapValues, findLastIndex } from 'lodash-es';

import { SmartChart } from '../../../../../components/Chart';
import ConsensusSourceText from '../../../../../components/ConsensusSourceText';

import {
  colorBySeries,
  nameBySeries,
  seriesOrder,
  setupChartConfig,
} from '../../config/kpi-forecast-chart';
import { format } from '../../../../../utils/date';

const padArray = (arr = [], length = 0) => arr.concat(Array(length - arr.length).fill(null));

const KpiForecastChart = ({
  view,
  series,
  chartHeight = 211,
  showSourceText = true,
  isScreenshot,
  chartId,
}) => {
  const { current: makeChartConfig } = useRef(setupChartConfig());
  const { published, metadata, categories, ...restSeries } = series;
  const activeCategory = categories && categories[1];

  const sortedSeries = seriesOrder.reduce((acc, seriesKey) => {
    if (restSeries[seriesKey]) {
      return {
        ...acc,
        [seriesKey]: restSeries[seriesKey],
      };
    }
    return acc;
  }, {});

  const metaSeriesData = mapValues(sortedSeries, (data, key) => ({
    name: nameBySeries[key],
    color: colorBySeries[key],
  }));

  const decoratedSeries = mapValues(sortedSeries, (data, key) => {
    const decoratedData = padArray(data, categories.length);
    const lastDataPointIndex = findLastIndex(decoratedData, (value) => !isNil(value));

    return decoratedData.map((value, index) => ({
      x: index,
      y: value === 0 ? null : value,
      ...(lastDataPointIndex === index
        ? {
            marker: { fillColor: colorBySeries[key], radius: 2 },
            dataLabels: {
              enabled: true,
            },
          }
        : {}),
    }));
  });

  const publishedDates = (categories || []).reduce(
    (acc, category, index) => ({
      ...acc,
      [category]: (published || [])[index] && format(published[index]),
    }),
    {}
  );

  const chartConfig = makeChartConfig({
    view,
    height: chartHeight,
    metadata,
    publishedDates,
    activeCategory,
    isScreenshot,
  });

  return (
    <div className="app-kpi-forecast-chart">
      <SmartChart
        chartId={chartId}
        series={decoratedSeries}
        metaSeriesData={metaSeriesData}
        categories={categories}
        config={chartConfig}
      />
      <div className="app-kpi-forecast-chart__footer">
        <div className="app-kpi-forecast-chart__legend">
          {map(sortedSeries, (_, key) => (
            <div key={key} className="legend-item">
              <span
                className="legend-item-marker"
                style={{ backgroundColor: colorBySeries[key] }}
              />
              <span>{nameBySeries[key]}</span>
            </div>
          ))}
        </div>
        {showSourceText && <ConsensusSourceText className="data-source-text" />}
      </div>
    </div>
  );
};

KpiForecastChart.propTypes = {
  series: T.object,
  view: T.string, // 'change', 'value'
  chartHeight: T.number,
  showSourceText: T.bool,
  isScreenshot: T.bool,
  chartId: T.string,
};

export default KpiForecastChart;
